import React, {useEffect, useState} from 'react';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {FormikProps} from 'formik';

interface OptionData {
    [key: string]: any;
}

interface SelectOption {
    label: string;
    value: any;
    [key: string]: any;
}

interface RequestParams {
    params?: Record<string, string>;
    [key: string]: any;
}

interface SearchSelectProps {
    optionsUrl: string;
    formik: FormikProps<any>;
    fieldName: string;
    requestParams?: RequestParams;
    isDisabled?: boolean;
    isClearable?: boolean;
    valueName?: string;
    labelName?: string;
    labelFormatter?: (label: string, obj?: OptionData) => string;
    addAdditionalData?: (obj: OptionData) => Record<string, any>;
    hideSelectedOptions?: boolean;
    selectKey?: string | number | null;
    getLabel?: ((value: any) => string) | null;
    isPaginated?: boolean;
}

const lookup = (o: any, p: string, nullProp = false): any => 
    p.split('.').reduce((a, v) => (nullProp ? a?.[v] : a[v]), o);

const loadRequest = async (url: string, params: any) => {
    try {
        return await axios.get(url, params);
    } catch (error: any) {
        if (error.response?.status === 400) {
            return error.response;
        }
        throw error;
    }
};

const loadRequestDebounced = AwesomeDebouncePromise(loadRequest, 280, {key: (url: string) => url});

const SearchSelect: React.FC<SearchSelectProps> = React.memo(({
    optionsUrl,
    formik,
    fieldName,
    requestParams = {},
    isDisabled = false,
    isClearable = false,
    valueName = 'value',
    labelName = 'label',
    labelFormatter = (label: string) => label,
    addAdditionalData = () => ({}),
    hideSelectedOptions = false,
    selectKey = null,
    getLabel = null,
    isPaginated = false,
}) => {
    const [selectedLabel, setSelectedLabel] = useState<string>('');

    const getOptions = async (inputValue: string): Promise<SelectOption[]> => {
        try {
            const params: Record<string, string> = {
                ...(requestParams?.params || {}),
            };
            
            if (inputValue) {
                params.query = inputValue;
            }
            
            const response = await loadRequestDebounced(optionsUrl, {
                ...requestParams,
                params,
            });
            
            if (!response || !response.data) {
                return [];
            }
            
            const responseData = response.data;
            
            let items: OptionData[] = [];
            
            if (isPaginated) {
                if (responseData.items && Array.isArray(responseData.items)) {
                    items = responseData.items;
                } else if (Array.isArray(responseData)) {
                    items = responseData;
                } else {
                    return [];
                }
            } else {
                if (Array.isArray(responseData)) {
                    items = responseData;
                } else if (responseData.items && Array.isArray(responseData.items)) {
                    items = responseData.items;
                } else {
                    return [];
                }
            }
            
            if (items.length === 0) {
                return [];
            }
            
            return items
                .map((item: OptionData) => {
                    const label = item[labelName];
                    const value = item[valueName];
                    
                    if (label === undefined || value === undefined) {
                        return null;
                    }
                    
                    return {
                        label: labelFormatter(String(label), item),
                        value: value,
                        ...addAdditionalData(item),
                    };
                })
                .filter((option): option is SelectOption => option !== null);
        } catch (error: any) {
            if (error.response?.status === 400 && error.response?.data) {
                return [];
            }
            console.error('SearchSelect: Error loading options:', error);
            return [];
        }
    };

    const value = lookup(formik.values, fieldName, true);

    useEffect(() => {
        const fetchData = async () => {
            const response = await axios.get(`${optionsUrl}/${value}`);
            const obj = response.data;
            setSelectedLabel(labelFormatter(obj[labelName], obj));
        };

        if (value && !selectedLabel) {
            if (getLabel !== null) {
                setSelectedLabel(labelFormatter(getLabel(value)));
            } else {
                fetchData();
            }
        }
    }, [
        value,
        optionsUrl,
        getLabel,
        labelName,
        selectedLabel,
        setSelectedLabel,
        labelFormatter,
    ]);

    const setSelectValue = (data: SelectOption | null) => {
        const val = data?.value ?? null;
        const label = data?.label ?? '';
        formik.setFieldValue(fieldName, val);
        setSelectedLabel(label);
    };

    return (
        <AsyncSelect
            key={selectKey}
            isDisabled={isDisabled}
            isClearable={isClearable}
            cacheOptions
            defaultOptions={!isDisabled}
            value={{
                label: selectedLabel,
                value,
            }}
            loadOptions={getOptions}
            hideSelectedOptions={hideSelectedOptions}
            onChange={(data: SelectOption | null) => setSelectValue(data)}
            onBlur={formik.handleBlur}
            styles={{
                menu: (provided: any) => ({
                    ...provided,
                    zIndex: 10,
                }),
                control: (provided: any) => {
                    if (lookup(formik.touched, fieldName, true) && lookup(formik.errors, fieldName, true)) {
                        return {
                            ...provided,
                            border: '1px solid #dc3545',
                        };
                    }
                    return provided;
                },
            }}
        />
    );
});

SearchSelect.displayName = 'SearchSelect';

export default SearchSelect; 